import type {
  ClientCompetitionListRow,
  Competition,
  CompetitionStatus,
  CompetitionType,
  EngagementModel,
  RegisteredStudentListItem,
  TeamVisibility,
} from "../types/database";
import { logAdminAction } from "./auditLog";
import { getImpersonatedCompanyIds, isImpersonating } from "./impersonate";
import { supabase } from "./supabase";

import { getMyCompanies } from "./companies";

function unwrapOne<T>(rel: T | T[] | null | undefined): T | null {
  if (rel == null) return null;
  return Array.isArray(rel) ? (rel[0] ?? null) : rel;
}

type CompetitionCompanyEmbed = { name: string; logo_url: string | null };

type CompetitionQueryRow = Competition & {
  companies: CompetitionCompanyEmbed | CompetitionCompanyEmbed[] | null;
  registrations: { count: number }[] | null;
  submissions: { count: number }[] | null;
  teams: { count: number }[] | null;
};

function mapClientCompetitionRow(row: CompetitionQueryRow): ClientCompetitionListRow {
  const { companies, registrations, submissions, teams, ...comp } = row;
  const company = unwrapOne(companies);
  const cn = company?.name;
  const logo = company?.logo_url;
  const rc = registrations?.[0]?.count;
  const sc = submissions?.[0]?.count;
  const tc = teams?.[0]?.count;
  return {
    ...(comp as Competition),
    company_name: typeof cn === "string" && cn.trim() ? cn : undefined,
    company_logo_url: typeof logo === "string" && logo.trim() ? logo : null,
    registration_count: typeof rc === "number" ? rc : 0,
    submission_count: typeof sc === "number" ? sc : 0,
    team_count: typeof tc === "number" ? tc : 0,
  };
}

function sortDashboardCompetitions(a: ClientCompetitionListRow, b: ClientCompetitionListRow): number {
  // Only the four "live" states get explicit ranks; new approval-flow
  // states (submitted/approved/etc) sort to the back via the ?? 99
  // fallback. The case-approval portal sorts those by submitted_at
  // anyway, so a sensible default here is enough.
  const order: Partial<Record<CompetitionStatus, number>> = { draft: 0, open: 1, judging: 2, closed: 3 };
  const sd = (order[a.status] ?? 99) - (order[b.status] ?? 99);
  if (sd !== 0) return sd;
  const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
  const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
  return tb - ta;
}

// PostgREST disambiguation: `team_members` and `submissions` both have FKs to
// `teams` AND `competitions`, so PostgREST sees two paths from competitions →
// teams (direct + via team_members) and competitions → submissions (direct +
// via teams). The default embed `teams(count)` returns HTTP 300 PGRST201
// "Multiple Choices". Force the direct FK by name.
const COMP_LIST_SELECT =
  "*, companies ( name, logo_url ), registrations ( count ), submissions!submissions_competition_id_fkey ( count ), teams!teams_competition_id_fkey ( count )";

export async function listMyCompetitions(): Promise<ClientCompetitionListRow[]> {
  // Impersonation branch: when an admin is viewing as a client, scope the
  // query to the target's companies via the SECURITY DEFINER helper.
  // Admin RLS already permits reading every competitions row, so the
  // explicit `company_id IN (...)` filter is what makes the result match
  // what the target user would see.
  if (isImpersonating()) {
    const companyIds = await getImpersonatedCompanyIds();
    if (companyIds.length === 0) return [];
    const { data, error } = await supabase
      .from("competitions")
      .select(COMP_LIST_SELECT)
      .in("company_id", companyIds);
    if (error || !data) return [];
    const rows = (data as CompetitionQueryRow[]).map(mapClientCompetitionRow);
    rows.sort(sortDashboardCompetitions);
    return rows;
  }

  // Trust RLS instead of pre-filtering by getMyCompanies() — the
  // "company members read own competitions" policy will already scope this
  // query to the company(s) the user belongs to. Pre-filtering created a
  // session-hydration race that hid newly-created cases right after a
  // redirect from /client/competition/new.
  const { data, error } = await supabase.from("competitions").select(COMP_LIST_SELECT);

  if (error || !data) return [];
  const rows = (data as CompetitionQueryRow[]).map(mapClientCompetitionRow);
  rows.sort(sortDashboardCompetitions);
  return rows;
}

export async function getClientCompetition(id: string): Promise<ClientCompetitionListRow | null> {
  // Trust RLS: if the user can read this competition row, they own it (or
  // they're an admin). Don't gate the query on a separate `getMyCompanies()`
  // call — that introduces a race where, immediately after redirecting from
  // a creation flow, the auth session may not yet be hydrated when this fires
  // and the embedded company_users query returns []. The competition is
  // already created, but the editor would render "Competition not found"
  // until the next focus.
  const { data, error } = await supabase
    .from("competitions")
    .select(COMP_LIST_SELECT)
    .eq("id", id)
    .maybeSingle();

  if (error || !data) return null;
  return mapClientCompetitionRow(data as CompetitionQueryRow);
}

export type CreateCompetitionPayload = {
  company_id: string;
  engagement_model: EngagementModel;
  name: string;
  brief?: string | null;
  deliverable_description?: string | null;
  status?: CompetitionStatus;
  opens_at?: string | null;
  closes_at?: string | null;
  team_size_min?: number;
  max_team_size?: number;
  competition_type?: CompetitionType;
  max_enrollment?: number | null;
  eligibility_schools?: string[] | null;
  eligibility_majors?: string[] | null;
  eligibility_grad_year_min?: number | null;
  eligibility_grad_year_max?: number | null;
};

export async function createCompetition(
  payload: CreateCompetitionPayload
): Promise<{ id: string | null; error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { id: null, error: new Error(userError?.message ?? "Not signed in") };
  }

  const row = {
    company_id: payload.company_id,
    engagement_model: payload.engagement_model,
    name: payload.name,
    brief: payload.brief ?? null,
    deliverable_description: payload.deliverable_description ?? null,
    status: payload.status ?? "draft",
    opens_at: payload.opens_at ?? null,
    closes_at: payload.closes_at ?? null,
    team_size_min: payload.team_size_min ?? 1,
    max_team_size: payload.max_team_size ?? 4,
    competition_type: payload.competition_type ?? "capped",
    max_enrollment: payload.max_enrollment ?? null,
    eligibility_schools: payload.eligibility_schools ?? null,
    eligibility_majors: payload.eligibility_majors ?? null,
    eligibility_grad_year_min: payload.eligibility_grad_year_min ?? null,
    eligibility_grad_year_max: payload.eligibility_grad_year_max ?? null,
    created_by: userData.user.id,
  };

  const { data, error } = await supabase.from("competitions").insert(row).select("id").single();
  if (error || !data?.id) return { id: null, error: new Error(error?.message ?? "Insert failed") };
  // Lazy-import to dodge any circular import with authContext if this
  // ever ends up bundled together; analytics is a graceful no-op when
  // the env key is missing so this never fails.
  void import("./analytics").then(({ track }) =>
    track("competition_created", {
      competition_id: data.id as string,
      engagement_model: payload.engagement_model,
      company_id: payload.company_id,
    })
  );
  await logAdminAction({
    action: "competition.create",
    entityType: "competitions",
    entityId: data.id as string,
    summary: `Created competition: ${payload.name} (${payload.engagement_model})`,
    diff: {
      after: {
        name: payload.name,
        company_id: payload.company_id,
        engagement_model: payload.engagement_model,
        status: payload.status ?? "draft",
      },
    },
  });
  return { id: data.id as string, error: null };
}

export async function updateCompetition(
  id: string,
  patch: Partial<
    Omit<Competition, "id" | "created_by"> & { status?: CompetitionStatus }
  >
): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("competitions").update(patch).eq("id", id);
  return { error: error ? new Error(error.message) : null };
}

export async function publishCompetition(id: string): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from("competitions")
    .update({ status: "open" })
    .eq("id", id)
    .eq("status", "draft");
  return { error: error ? new Error(error.message) : null };
}

export async function moveCompetitionToJudging(id: string): Promise<{ error: Error | null }> {
  // CAS guard (audit L-4): only an `open` competition may advance to judging, so
  // this can't jump an arbitrary status into judging. `.select()` lets us treat
  // a 0-row match as a real error instead of a silent no-op.
  // NOTE: 'open' is the current live status — revisit if the C-1 lifecycle
  // decision changes how a case becomes live.
  const { data, error } = await supabase
    .from("competitions")
    .update({ status: "judging" })
    .eq("id", id)
    .eq("status", "open")
    .select("id");
  if (error) return { error: new Error(error.message) };
  if (!data || data.length === 0) {
    return { error: new Error("Only an open competition can be moved to judging.") };
  }
  return { error: null };
}

export async function deleteCompetition(id: string): Promise<{ error: Error | null }> {
  // Snapshot before-state for the audit trail. RLS-scoped — clients see
  // their own row; admins see everyone's. Either branch is fine for the
  // audit summary.
  const { data: before } = await supabase
    .from("competitions")
    .select("id, name, company_id, status")
    .eq("id", id)
    .maybeSingle();
  const { error } = await supabase.from("competitions").delete().eq("id", id);
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "competition.delete",
    entityType: "competitions",
    entityId: id,
    summary: `Deleted competition “${(before as { name?: string } | null)?.name ?? id.slice(0, 8)}”`,
    diff: { before: (before as Record<string, unknown> | null) ?? null, after: null },
  });
  return { error: null };
}

/**
 * Registered students for the case-editor "Roster" panel. Uses parallel
 * separate queries instead of a PostgREST embed on `students` — the embed
 * silently returned [] for client users when the RLS race that bit
 * `listTopCandidates` was active. Same fix pattern.
 */
export async function listRegisteredStudents(competitionId: string): Promise<RegisteredStudentListItem[]> {
  const { data: regs, error: regsErr } = await supabase
    .from("registrations")
    .select("student_id, created_at")
    .eq("competition_id", competitionId)
    .eq("status", "registered")
    .order("created_at", { ascending: true });

  if (regsErr) {
    console.warn("listRegisteredStudents: registrations query failed", regsErr);
    return [];
  }
  if (!regs?.length) return [];

  const studentIds = regs.map((r) => r.student_id as string);

  const [{ data: students }, { data: profiles }] = await Promise.all([
    supabase.from("students").select("id, university").in("id", studentIds),
    supabase.from("user_profiles").select("id, full_name").in("id", studentIds),
  ]);

  const universityById = new Map<string, string | null>();
  for (const s of (students ?? []) as { id: string; university: string | null }[]) {
    universityById.set(s.id, s.university ?? null);
  }
  const nameById = new Map<string, string | null>();
  for (const p of (profiles ?? []) as { id: string; full_name: string | null }[]) {
    nameById.set(p.id, p.full_name ?? null);
  }

  return regs.map((r) => {
    const sid = r.student_id as string;
    return {
      student_id: sid,
      full_name: nameById.get(sid) ?? null,
      university: universityById.get(sid) ?? null,
    };
  });
}

export type TeamSummaryRow = {
  team_id: string;
  team_name: string;
  visibility: TeamVisibility;
  is_locked: boolean;
  member_count: number;
  submitted_count: number;
  total_deliverables: number;
};

type TeamSummaryBaseRow = {
  id: string;
  name: string;
  visibility: TeamVisibility | null;
  is_locked: boolean | null;
};

export async function listTeamsForCompetition(competitionId: string): Promise<TeamSummaryRow[]> {
  const { data: teams, error } = await supabase
    .from("teams")
    .select("id, name, visibility, is_locked")
    .eq("competition_id", competitionId)
    .order("name", { ascending: true });

  if (error || !teams?.length) return [];

  const teamRows = teams as TeamSummaryBaseRow[];
  const teamIds = teamRows.map((t) => t.id);

  const [{ data: memberRows }, { data: submissionRows }, { data: deliverableRows }] = await Promise.all([
    supabase.from("team_members").select("team_id").in("team_id", teamIds),
    supabase.from("submissions").select("team_id, deliverable_id").in("team_id", teamIds),
    supabase.from("deliverables").select("id").eq("competition_id", competitionId),
  ]);

  const memberCounts = new Map<string, number>();
  for (const m of memberRows ?? []) {
    memberCounts.set(m.team_id, (memberCounts.get(m.team_id) ?? 0) + 1);
  }

  const submittedSets = new Map<string, Set<string>>();
  for (const s of submissionRows ?? []) {
    if (!submittedSets.has(s.team_id)) submittedSets.set(s.team_id, new Set<string>());
    submittedSets.get(s.team_id)?.add(s.deliverable_id);
  }

  const totalDeliverables = (deliverableRows ?? []).length;

  return teamRows.map((t) => ({
    team_id: t.id,
    team_name: t.name,
    visibility: t.visibility === "private" ? "private" : "public",
    is_locked: Boolean(t.is_locked),
    member_count: memberCounts.get(t.id) ?? 0,
    submitted_count: submittedSets.get(t.id)?.size ?? 0,
    total_deliverables: totalDeliverables,
  }));
}
